import { useEffect, useState } from 'react';
import {
  Edit, SimpleForm, TextInput, useGetList, Loading, useNotify,
  useDataProvider, required,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography, Box, Button, CircularProgress } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { ItalicHeadlineInput } from '../../components/ItalicHeadlineInput.jsx';

const RESOURCE = 'home-welcome';

// Default copy used when initialising the section for the first time.
const DEFAULTS = {
  eyebrow: 'Welcome',
  rawTitle: 'Discover <em>Maple Leaf</em>',
  body: 'Aspiring to be one of the great schools of South Asia, Maple Leaf International School offers a uniquely rewarding educational experience. Through our focus on thoughtful teaching and learning, students discover more, achieve more and become more.',
  primaryButtonLabel: 'About Us',
  primaryButtonUrl: '/about',
  secondaryButtonLabel: 'From the Principal',
  secondaryButtonUrl: '/page/principals-message',
};

/**
 * The Welcome section is a singleton — exactly one row. The "list" view
 * immediately routes to the edit page for that row. If no row exists yet
 * (e.g. fresh DB), we offer a one-click initialise.
 */
const WelcomeRedirect = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { data, isLoading } = useGetList(RESOURCE, {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (data && data.length > 0) {
      navigate(`/${RESOURCE}/${data[0].id}`, { replace: true });
    }
  }, [data, navigate]);

  if (isLoading) return <Loading />;
  if (data && data.length > 0) return null;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const created = await dataProvider.create(RESOURCE, { data: { ...DEFAULTS } });
      navigate(`/${RESOURCE}/${created.data.id}`, { replace: true });
    } catch (err) {
      notify(`Failed to initialise Welcome section: ${err.message}`, { type: 'error' });
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Welcome section not yet initialised</Typography>
      <Button variant="contained" onClick={handleCreate} disabled={creating}>
        {creating ? <CircularProgress size={18} /> : 'Initialise Welcome section'}
      </Button>
    </Box>
  );
};

const WelcomeForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 720 }}>
    <Typography variant="overline" color="text.secondary">
      Homepage — "Discover Maple Leaf" welcome section
    </Typography>
    <TextInput
      source="eyebrow"
      label="Eyebrow"
      fullWidth
      helperText='Small uppercase label above the title — e.g. "Welcome".'
    />
    <ItalicHeadlineInput source="rawTitle" label="Title" validate={required()} />
    <TextInput
      source="body"
      label="Body text"
      multiline
      minRows={4}
      fullWidth
      helperText="The paragraph shown beside the title."
    />

    <Typography variant="overline" color="text.secondary" sx={{ pt: 2 }}>Buttons</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput source="primaryButtonLabel" label="Primary button label" fullWidth />
      <TextInput
        source="primaryButtonUrl"
        label="Primary button link"
        fullWidth
        helperText='Internal path, e.g. "/about".'
      />
    </Stack>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput source="secondaryButtonLabel" label="Secondary button label" fullWidth />
      <TextInput
        source="secondaryButtonUrl"
        label="Secondary button link"
        fullWidth
        helperText='Internal path, e.g. "/page/principals-message".'
      />
    </Stack>
  </Stack>
);

const WelcomeEdit = () => (
  <Edit redirect={false} mutationMode="pessimistic" actions={false}>
    <SimpleForm><WelcomeForm /></SimpleForm>
  </Edit>
);

export default {
  list: WelcomeRedirect,
  edit: WelcomeEdit,
  icon: HomeIcon,
  options: { label: 'Welcome' },
};
